/**
 * Shared layout primitives for `/entity/:entityId` show pages, to enforce
 * consistency.
 */
import { Color, Spacing } from '@persona-id/design-tokens';
import { MEDIA_QUERIES, MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Hairline } from 'design-system/variables/effect';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Backdrop = styled.div`
  position: relative;
  width: 100%;
  padding: ${Spacing[7]} ${Spacing[5]};

  display: flex;
  flex-direction: column;
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

export const SoloColumn = styled.div`
  width: 100%;
  min-width: 710px;
  float: left;
  padding-top: ${Spacing[2]};
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[5]};
`;

export const MainColumn = styled.div`
  width: 67%;
  min-width: 710px;
  float: left;
  padding: ${Spacing[2]} ${Spacing[4]} 0 0;
  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    width: 100%;
  }
`;

export const LeftColumn = styled.div`
  width: 33%;
  float: right;
  padding-left: ${Spacing[5]};
  padding-top: ${Spacing[2]};
  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    display: none;
  }
`;

export const RightColumn = styled.div`
  width: 33%;
  float: left;
  padding-right: ${Spacing[5]};
  padding-top: ${Spacing[2]};
  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    display: none;
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-gap: ${Spacing[6]};
`;

export const HeaderContainer = styled.div`
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[5]};
  padding-bottom: ${Spacing[5]};
  z-index: ${Z_INDICES.topNav};
`;

/**
 * For features like WorkflowEditor and InquiryTemplateEditor.
 * Can be used to place headers in the same container as banners and other
 * natively sticky elements to make them a sticky group.
 * */
export const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: ${Z_INDICES.banner};
`;

/**
 * For features like WorkflowEditor and InquiryTemplateEditor whose content
 * spans the whole screen.
 */
export const FullScreenHeaderContainer = styled(HeaderContainer)`
  display: flex;
  justify-content: space-between;
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${Color.CoreSlate50};
  box-shadow: ${Hairline.Bottom};
  position: relative; /* Allows box shadow to show */
`;

/**
 * For use in fixed size calculations. This is the height when used "normally,"
 * e.g. with buttons with `sz="sm"``.
 */
export const FULL_SCREEN_HEADER_HEIGHT = 64;
/**
 * For use in fixed size calculations. Used in
 * headers with buttons `sz="xs"`` (e.g ITE)
 */
export const FULL_SCREEN_HEADER_HEIGHT_XS = 64;

export const CardContainer = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-template-columns: 1fr 1fr 1fr;

  @media ${MEDIA_QUERIES.isMobile} {
    grid-template-columns: none;
    grid-auto-flow: row;
  }
`;
